import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { MiLogoMark } from "@/components/MiLogoMark";
import { ArrowRight, Check } from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { pageHead } from "@/i18n/head";
import { arbejdspladsStrings } from "@/i18n/content/arbejdsplads";
import { Img, SIZES } from "@/components/Img";

/** <head> for the "MI Support as a workplace" career page. */
export function arbejdspladsHead(locale: Locale) {
  const s = arbejdspladsStrings[locale];
  return pageHead("arbejdsplads", locale, s.meta, { breadcrumbName: s.meta.title });
}

export function ArbejdspladsPage({ locale }: { locale: Locale }) {
  const t = arbejdspladsStrings[locale];

  return (
    <SiteLayout>
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-foreground/70">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight flex items-baseline justify-center flex-wrap gap-2 md:gap-3">
            <MiLogoMark className="h-[0.85em] w-auto translate-y-[0.18em]" aria-label="MI Support" />
            <span>{t.hero.titleSuffix}</span>
          </h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.body}</p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-10 md:grid-cols-2 items-center">
          <Img src="/wp-content/uploads/MISupportIT_NTK4135-1-scaled.jpg" alt="MI Support" className="rounded-2xl w-full aspect-[4/3] object-cover" sizes={SIZES.split} />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight flex items-baseline flex-wrap gap-1 md:gap-2">
              {t.team.headingPrefix} <MiLogoMark className="h-8 md:h-10 w-auto translate-y-[0.18em]" aria-label="MI Support" />
            </h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.team.body}</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid gap-8 md:grid-cols-3 text-center">
          {t.stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-secondary p-10">
              <div className="text-5xl md:text-6xl font-bold text-white">{s.value}</div>
              <p className="mt-4 text-white/90">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="mx-auto max-w-4xl px-4 md:px-8 mt-12">
          <p className="text-foreground/85 leading-relaxed">{t.staffIntro}</p>
          <blockquote className="mt-8 border-l-4 border-primary pl-6 italic text-lg">
            "{t.quote1.text}"
            <footer className="mt-2 not-italic text-sm text-foreground/70">— {t.quote1.attrib}</footer>
          </blockquote>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid gap-12 md:grid-cols-2">
          {t.pillars.map((p) => (
            <div key={p.heading}>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{p.heading}</h2>
              <p className="mt-4 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <blockquote className="border-l-4 border-primary pl-6 italic text-xl">
            "{t.quote2.text}"
            <footer className="mt-2 not-italic text-sm text-foreground/70">— {t.quote2.attrib}</footer>
          </blockquote>

          <h3 className="mt-12 text-2xl font-bold tracking-tight">{t.wellbeing.heading}</h3>
          <p className="mt-4 text-foreground/85 leading-relaxed">{t.wellbeing.body}</p>
          <ul className="mt-4 space-y-3 text-foreground/85">
            {t.wellbeing.items.map((item) => (
              <li key={item} className="flex gap-3"><Check className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>{item}</span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <Link to={pathFor("karriere", locale)} className="mt-10 btn-cta">
            {t.cta.button} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
